import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/ui-kit";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

// Saves an authenticated blob response with the filename the server picked, the same pattern
// used across the app (see Compliance.jsx's downloadRoster) for any file behind the auth header.
function saveBlob(res, fallbackName) {
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackName;
  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
}

export default function Claims() {
  const [dateFrom, setDateFrom] = useState(firstOfMonthIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [payer, setPayer] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { main_count, special_count, total_count, main_filename, special_filename }
  const [problems, setProblems] = useState(null); // null = not previewed yet, [] = clean
  const [downloading, setDownloading] = useState(null); // "main" | "special" | null

  const preview = () => {
    setLoading(true);
    setResult(null);
    setProblems(null);
    api
      .get("/claims/export", { params: { from: dateFrom, to: dateTo, payer: payer || undefined } })
      .then((r) => {
        setResult(r.data);
        setProblems([]);
      })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (detail && Array.isArray(detail.problems)) {
          setProblems(detail.problems);
        } else {
          toast.error(formatApiError(detail));
        }
      })
      .finally(() => setLoading(false));
  };

  const download = async (file) => {
    setDownloading(file);
    try {
      const res = await api.get("/claims/export", {
        params: { from: dateFrom, to: dateTo, payer: payer || undefined, file },
        responseType: "blob",
      });
      saveBlob(res, file === "main" ? "claims-main.xls" : "claims-special.xls");
    } catch (e) {
      toast.error("Could not generate the file. Run Preview again to see what changed.");
    } finally {
      setDownloading(null);
    }
  };

  const clean = problems && problems.length === 0;

  return (
    <div data-testid="claims-page">
      <PageHeader subtitle="Reporting" title="Claims Export" />

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm"
              data-testid="claims-from"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm"
              data-testid="claims-to"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Payer (optional)</label>
            <input
              type="text"
              placeholder="e.g. UCare"
              value={payer}
              onChange={(e) => setPayer(e.target.value)}
              className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm w-40"
              data-testid="claims-payer"
            />
          </div>
          <button onClick={preview} disabled={loading} className="btn-primary disabled:opacity-60 flex items-center gap-1.5" data-testid="claims-preview-btn">
            <Search size={14} /> {loading ? "Checking..." : "Preview"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Builds the ClaimMD file from this agency's completed rides in the range above. Nothing downloads until every row passes
          the checks below, exactly like the Sunday billing run.
        </p>
      </div>

      {problems && problems.length > 0 && (
        <div className="bg-white border border-rose-200 rounded-md p-4 mb-4" data-testid="claims-problems">
          <div className="text-sm font-semibold text-rose-700 mb-2">
            {problems.length} problem{problems.length === 1 ? "" : "s"} found, nothing to download yet
          </div>
          <TableShell columns={["Ride", "Field", "Problem"]}>
            {problems.map((p, i) => (
              <tr key={i} className="tbl-row border-t border-slate-100">
                <td className="px-4 py-2.5 font-mono text-xs">{p.ride_id || "(agency-wide)"}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{p.field}</td>
                <td className="px-4 py-2.5 text-sm">{p.message}</td>
              </tr>
            ))}
          </TableShell>
        </div>
      )}

      {clean && result && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="claims-clean-result">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-slate-50 rounded-md p-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Main</div>
              <div className="font-display text-xl font-bold text-[#0A192F] mt-1">{result.main_count}</div>
            </div>
            <div className="bg-slate-50 rounded-md p-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Special</div>
              <div className="font-display text-xl font-bold text-[#0A192F] mt-1">{result.special_count}</div>
            </div>
            <div className="bg-slate-50 rounded-md p-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Total</div>
              <div className="font-display text-xl font-bold text-[#0A192F] mt-1">{result.total_count}</div>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => download("main")}
              disabled={result.main_count === 0 || downloading === "main"}
              className="btn-primary disabled:opacity-60 flex items-center gap-1.5"
              data-testid="claims-download-main"
            >
              <Download size={14} /> {downloading === "main" ? "Preparing..." : "Download main file"}
            </button>
            {result.special_count > 0 && (
              <button
                onClick={() => download("special")}
                disabled={downloading === "special"}
                className="border border-slate-300 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-60"
                data-testid="claims-download-special"
              >
                <Download size={14} /> {downloading === "special" ? "Preparing..." : "Download special file"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
